import React, { useState, useEffect } from 'react';
import SiridhariniBlogService from '../../../services/siridhariniBlogService';

const BlogSidebar = ({ 
  onSearch, 
  onCategoryFilter, 
  searchTerm: parentSearchTerm = '', 
  selectedCategory: parentSelectedCategory = '' 
}) => {
  const [searchTerm, setSearchTerm] = useState(parentSearchTerm);
  const [selectedCategory, setSelectedCategory] = useState(parentSelectedCategory);
  const [recentPosts, setRecentPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadSidebarData();
  }, []);

  useEffect(() => {
    setSearchTerm(parentSearchTerm);
  }, [parentSearchTerm]);

  useEffect(() => {
    setSelectedCategory(parentSelectedCategory);
  }, [parentSelectedCategory]);

  const loadSidebarData = async () => {
    try {
      setLoading(true);
      const [blogs, categoriesData] = await Promise.all([
        SiridhariniBlogService.getAllPublishedBlogs(),
        SiridhariniBlogService.getAllCategories()
      ]);
      
      // Get recent 5 posts
      const recent = blogs.slice(0, 5).map(blog => ({
        title: blog.title,
        id: blog.id,
        slug: blog.slug
      }));
      
      setRecentPosts(recent);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading sidebar data:', error);
      setRecentPosts([]);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (onSearch) {
      onSearch(searchTerm);
    }
  };

  const handleCategoryClick = (category) => {
    const newCategory = category === selectedCategory ? '' : category;
    setSelectedCategory(newCategory);
    if (onCategoryFilter) {
      onCategoryFilter(newCategory);
    }
  };

  const archives = [
    "November 2021",
    "August 2021", 
    "December 2018"
  ];

  const articleTags = [
    "AGRO", "CROP", "CULTIVATION", "ECO", "ENVIRONMENT", "FARMING", "FOOD",
    "GREEN", "HARVESTING", "HORTICULTURE", "PROFITS", "REALTY", "SANDALWOOD",
    "TOUCHSTONE", "WEALTH"
  ];

  const calendarData = {
    month: "October 2025",
    days: [
      { date: 1, day: "W" }, { date: 2, day: "T" }, { date: 3, day: "F" }, { date: 4, day: "S" }, { date: 5, day: "S" },
      { date: 6, day: "M" }, { date: 7, day: "T" }, { date: 8, day: "W" }, { date: 9, day: "T" }, { date: 10, day: "F" },
      { date: 11, day: "S" }, { date: 12, day: "S" }, { date: 13, day: "M" }, { date: 14, day: "T" }, { date: 15, day: "W" },
      { date: 16, day: "T" }, { date: 17, day: "F" }, { date: 18, day: "S" }, { date: 19, day: "S" }, { date: 20, day: "M" },
      { date: 21, day: "T" }, { date: 22, day: "W" }, { date: 23, day: "T" }, { date: 24, day: "F" }, { date: 25, day: "S" },
      { date: 26, day: "S" }, { date: 27, day: "M" }, { date: 28, day: "T" }, { date: 29, day: "W" }, { date: 30, day: "T" },
      { date: 31, day: "F" }
    ]
  };

  const styles = {
    sidebar: {
      display: 'flex',
      flexDirection: 'column',
      gap: '3rem'
    },
    sidebarWidget: {
      backgroundColor: '#f8f9fa',
      padding: '2rem',
      borderRadius: '0'
    },
    widgetTitle: {
      fontSize: '1.5rem',
      fontWeight: 'bold',
      color: '#333',
      marginBottom: '1.5rem',
      textTransform: 'uppercase'
    },
    searchBox: {
      width: '100%',
      padding: '1rem',
      border: '1px solid #ddd',
      borderRadius: '0',
      fontSize: '1rem'
    },
    searchBtn: {
      marginTop: '1rem',
      padding: '0.8rem 2rem',
      backgroundColor: '#333',
      color: 'white',
      border: 'none',
      borderRadius: '0',
      fontSize: '1rem',
      cursor: 'pointer',
      textTransform: 'uppercase',
      fontWeight: 'bold',
      transition: 'all 0.3s ease'
    },
    listItem: {
      padding: '1rem 0',
      borderBottom: '1px solid #eee',
      fontSize: '0.95rem',
      color: '#333',
      cursor: 'pointer',
      transition: 'color 0.3s ease'
    },
    listItemLast: {
      borderBottom: 'none'
    },
    categoryItem: {
      padding: '0.8rem 1rem',
      borderBottom: '1px solid #eee',
      fontSize: '0.95rem',
      color: '#333',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      backgroundColor: 'transparent'
    },
    categoryItemActive: {
      backgroundColor: '#E67E22',
      color: 'white'
    },
    calendar: {
      backgroundColor: '#f8f9fa',
      padding: '2rem',
      borderRadius: '0'
    },
    calendarHeader: {
      fontSize: '1.2rem',
      fontWeight: 'bold',
      textAlign: 'center',
      marginBottom: '1rem',
      textTransform: 'uppercase'
    },
    calendarGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(7, 1fr)',
      gap: '0.5rem',
      fontSize: '0.9rem'
    },
    calendarDayHeader: {
      textAlign: 'center',
      fontWeight: 'bold',
      padding: '0.5rem 0',
      fontSize: '0.8rem'
    },
    calendarDay: {
      textAlign: 'center',
      padding: '0.5rem',
      cursor: 'pointer'
    },
    tagsContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.5rem'
    },
    tag: {
      backgroundColor: '#e9ecef',
      color: '#333',
      padding: '0.5rem 1rem',
      fontSize: '0.8rem',
      fontWeight: 'bold',
      textTransform: 'uppercase',
      borderRadius: '15px',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    recentComments: {
      fontSize: '1.5rem',
      fontWeight: 'bold',
      color: '#333',
      marginBottom: '1.5rem',
      textTransform: 'uppercase'
    }
  };

  return (
    <>
      <style>{`
        .search-btn:hover {
          background-color: #E67E22 !important;
        }
        
        .list-item:hover {
          color: #E67E22 !important;
        }
        
        .category-item:hover {
          background-color: #E67E22 !important;
          color: white !important;
        }
        
        .tag:hover {
          background-color: #E67E22 !important;
          color: white !important;
        }
        
        @media (max-width: 768px) {
          .sidebar {
            gap: 2rem !important;
          }
        }
      `}</style>
      
      <aside style={styles.sidebar} className="sidebar">
        {/* Search */}
        <div style={styles.sidebarWidget}>
          <h3 style={styles.widgetTitle}>Search</h3>
          <input 
            type="text" 
            style={styles.searchBox} 
            placeholder="Search blogs..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
          <button 
            style={styles.searchBtn}
            className="search-btn"
            onClick={handleSearch}
          >
            Search
          </button>
        </div>

        {/* Recent Posts */}
        <div style={styles.sidebarWidget}>
          <h3 style={styles.widgetTitle}>Recent Posts</h3>
          {loading ? (
            <div style={{ fontSize: '0.9rem', color: '#666' }}>Loading...</div>
          ) : (
            recentPosts.map((post, index) => (
              <div 
                key={index} 
                style={{
                  ...styles.listItem, 
                  ...(index === recentPosts.length - 1 ? styles.listItemLast : {})
                }}
                className="list-item"
              >
                {post.title}
              </div>
            ))
          )}
        </div>

        {/* Archives */}
        <div style={styles.sidebarWidget}>
          <h3 style={styles.widgetTitle}>Archives</h3>
          {archives.map((archive, index) => (
            <div 
              key={index} 
              style={{
                ...styles.listItem, 
                ...(index === archives.length - 1 ? styles.listItemLast : {})
              }}
              className="list-item"
            >
              {archive}
            </div>
          ))}
        </div>

        {/* Categories */}
        <div style={styles.sidebarWidget}>
          <h3 style={styles.widgetTitle}>Categories</h3>
          {loading ? (
            <div style={{ fontSize: '0.9rem', color: '#666' }}>Loading...</div>
          ) : (
            categories.map((category, index) => (
              <div 
                key={index} 
                style={{
                  ...styles.categoryItem,
                  ...(category === selectedCategory ? styles.categoryItemActive : {}),
                  ...(index === categories.length - 1 ? styles.listItemLast : {})
                }}
                className="category-item"
                onClick={() => handleCategoryClick(category)}
              >
                {category}
              </div>
            ))
          )}
        </div>

        {/* Recent Comments */}
        <div style={styles.sidebarWidget}>
          <h3 style={styles.recentComments}>Recent Comments</h3>
          <div style={{ fontSize: '0.9rem', color: '#666', fontStyle: 'italic' }}>
            No recent comments
          </div>
        </div>

        {/* Calendar Widget */}
        <div style={styles.calendar}>
          <h3 style={styles.calendarHeader}>{calendarData.month}</h3>
          <div style={styles.calendarGrid}>
            <div style={styles.calendarDayHeader}>M</div>
            <div style={styles.calendarDayHeader}>T</div>
            <div style={styles.calendarDayHeader}>W</div>
            <div style={styles.calendarDayHeader}>T</div>
            <div style={styles.calendarDayHeader}>F</div>
            <div style={styles.calendarDayHeader}>S</div>
            <div style={styles.calendarDayHeader}>S</div>
            
            {calendarData.days.map((day, index) => (
              <div key={index} style={styles.calendarDay}>{day.date}</div>
            ))}
          </div>
          <div style={{textAlign: 'center', marginTop: '1rem', fontSize: '0.8rem'}}>
            « Nov
          </div>
        </div>

        {/* Article Tags */}
        <div style={styles.sidebarWidget}>
          <h3 style={styles.widgetTitle}>Article Tags</h3>
          <div style={styles.tagsContainer}>
            {articleTags.map((tag, index) => (
              <span key={index} style={styles.tag} className="tag">
                {tag}
              </span>
            ))}
          </div>
        </div>
      </aside>
    </>
  );
};

export default BlogSidebar;